// CockpitFAB - Bouton flottant "+" pour publier
// Ouvre un menu "Que veux-tu publier ?" avec les options de création
// Inclut l'option Notes Vocales (micro) qui ouvre VoiceNotesSuperpouvoir
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, X, Mic, FileText, Store, Megaphone, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import VoiceNotesSuperpouvoir from '../ai/VoiceNotesSuperpouvoir';
import CreatePublicationModal from '../publish/CreatePublicationModal';

export default function CockpitFAB() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPublishMenu, setShowPublishMenu] = useState(false);
  const [showVoiceNotes, setShowVoiceNotes] = useState(false);
  const [showPublicationModal, setShowPublicationModal] = useState(false);
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  
  // Pages où le FAB ne doit PAS apparaître
  const hiddenPaths = [
    '/cockpit-pro',
    '/cockpit-particulier',
    '/cockpit-demo',
    '/abracadaworld',
    '/decrire-projet',
  ];
  
  // Vérifier si on doit masquer le FAB (inclut la page d'accueil exacte "/")
  const shouldHide = hiddenPaths.some(path => location.pathname.startsWith(path)) || location.pathname === '/';
  
  // Déterminer si on est dans l'Espace Pro
  const isInEspacePro = location.pathname.startsWith('/pro');
  
  // Détection du scroll pour afficher le FAB après le hero sur Espace Pro
  useEffect(() => {
    if (!isInEspacePro) {
      setScrolledPastHero(true);
      return;
    }
    
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setScrolledPastHero(scrollY > 800);
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isInEspacePro]);
  
  // Reset quand on change de page
  useEffect(() => {
    setScrolledPastHero(!isInEspacePro);
    setShowPublishMenu(false);
  }, [location.pathname, isInEspacePro]);
  
  if (shouldHide) return null;
  if (isInEspacePro && !scrolledPastHero) return null;

  // Options de publication
  const publishOptions = [
    { 
      id: 'annonce', 
      label: 'Annonce', 
      description: 'Vends, loue ou donne',
      icon: Megaphone, 
      color: '#F97316',
      bgColor: 'from-orange-50 to-orange-100/50',
      borderColor: 'border-orange-200',
      action: 'deposer'
    },
    { 
      id: 'boutique', 
      label: 'Louer', 
      description: 'Une boutique virtuelle',
      icon: Store, 
      color: '#8B5CF6',
      bgColor: 'from-purple-50 to-purple-100/50',
      borderColor: 'border-purple-200',
      path: '/pro#tarifs'
    },
    { 
      id: 'post', 
      label: 'Publier', 
      description: 'Un post sur les réseaux',
      icon: FileText, 
      color: '#3B82F6',
      bgColor: 'from-blue-50 to-blue-100/50',
      borderColor: 'border-blue-200',
      action: 'publish'
    },
    { 
      id: 'voix', 
      label: 'Notes vocales', 
      description: 'Réunion, RDV, dictée',
      icon: Mic, 
      color: '#1E3A5F',
      bgColor: 'from-[#1E3A5F]/5 to-[#1E3A5F]/10',
      borderColor: 'border-[#1E3A5F]/20',
      action: 'voice'
    },
  ];

  const handleOptionClick = (option) => {
    setShowPublishMenu(false);
    if (option.action === 'voice') {
      setShowVoiceNotes(true);
    } else if (option.action === 'publish') {
      setShowPublicationModal(true);
    } else if (option.action === 'deposer') {
      // Naviguer directement vers la page Déposer une annonce (standalone)
      navigate('/deposer-annonce');
    } else if (option.path) {
      navigate(option.path);
    }
  };

  // Get auth token
  const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token') || localStorage.getItem('user_token');

  return (
    <>
      {/* Keyframes */}
      <style>{`
        @keyframes borderPulse {
          0%, 100% { 
            box-shadow: 0 4px 15px rgba(30, 58, 95, 0.4), 0 0 0 0 rgba(249, 115, 22, 0.4);
          }
          50% { 
            box-shadow: 0 4px 15px rgba(30, 58, 95, 0.4), 0 0 0 4px rgba(249, 115, 22, 0.2);
          }
        }
      `}</style>

      {/* ══════════════════════════════════════════════════════════════════
          BOUTON FLOTTANT "+" - Publier (caché quand modal publication ouverte)
      ══════════════════════════════════════════════════════════════════ */}
      {!showPublicationModal && (
        <motion.button
          onClick={() => setShowPublishMenu(true)}
          className="fixed z-[9990] flex items-center justify-center"
          style={{
            bottom: '90px',
            right: '16px',
            width: '54px',
            height: '54px',
            borderRadius: '14px',
            background: '#1E3A5F',
            border: '2.5px solid #F97316',
            animation: 'borderPulse 2s ease-in-out infinite',
          }}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
          data-testid="publish-fab"
        >
          <Plus size={42} color="#F97316" strokeWidth={3.5} />
        </motion.button>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MENU "QUE VEUX-TU PUBLIER ?"
      ══════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showPublishMenu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/60 flex items-end justify-center"
            onClick={() => setShowPublishMenu(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-t-3xl overflow-hidden relative"
              style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}
            >
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>
              
              {/* Bouton X en haut à droite */}
              <button
                onClick={() => setShowPublishMenu(false)}
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <X size={18} className="text-gray-600" />
              </button>
              
              {/* Header */}
              <div className="px-6 pb-4 text-center">
                <h2 className="text-xl font-bold text-gray-900">Que veux-tu publier ?</h2>
                <p className="text-sm text-gray-500 mt-1">Choisis le type de contenu à créer</p>
              </div>
              
              {/* Options - Grille 2x2 carrés */}
              <div className="px-4 grid grid-cols-2 gap-3">
                {publishOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleOptionClick(option)}
                      className={`flex flex-col items-center justify-center gap-2 p-4 bg-gradient-to-br ${option.bgColor} rounded-2xl border-2 ${option.borderColor} hover:border-opacity-80 transition-all active:scale-[0.98] aspect-square`}
                    >
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: option.color }}
                      >
                        <Icon size={24} className="text-white" />
                      </div>
                      <div className="text-center">
                        <h3 className="font-bold text-gray-900 text-sm">{option.label}</h3>
                        <p className="text-[10px] text-gray-500 mt-0.5">{option.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
              
              {/* Info IA pour Notes vocales */}
              <div className="px-4 mt-3">
                <div className="bg-[#1E3A5F]/5 rounded-xl p-3 text-center">
                  <p className="text-xs text-[#1E3A5F]">
                    <span className="font-semibold">Notes vocales :</span> L'IA détecte et classe automatiquement tâches, notes, idées, RDV, email, devis
                  </p>
                </div>
              </div>
              
              {/* Bouton Fermer */}
              <div className="px-4 mt-4">
                <button
                  onClick={() => setShowPublishMenu(false)}
                  className="w-full py-3 text-center font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════════════════════════════════════════════════════════════════
          MODAL VOICE NOTES - Superpouvoir
      ══════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showVoiceNotes && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowVoiceNotes(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
            />
            
            {/* Modal VoiceNotes */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed z-[9999] bg-white rounded-2xl shadow-2xl overflow-hidden"
              style={{
                bottom: '80px',
                right: '16px',
                left: '16px',
                maxWidth: '500px',
                maxHeight: 'calc(100vh - 120px)',
                margin: '0 auto',
              }}
            >
              <VoiceNotesSuperpouvoir
                userId={localStorage.getItem('user_id') || 'demo_user'}
                onClose={() => setShowVoiceNotes(false)}
                onResult={(result) => {
                  console.log('VoiceNotes result:', result);
                  setShowVoiceNotes(false);
                }}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ══════════════════════════════════════════════════════════════════
          MODAL PUBLICATION - Article, Photo, Vidéo, Réel, Avant/Après
      ══════════════════════════════════════════════════════════════════ */}
      <CreatePublicationModal
        isOpen={showPublicationModal}
        onClose={() => setShowPublicationModal(false)}
        onSuccess={() => setShowPublicationModal(false)}
        token={token}
      />
    </>
  );
}
